// Single source of truth for how a diligencia reads in the UI — the dot/pill
// color, the human label, the explanatory subtext, the sidebar Badge kind, and
// the Ledger row tint. Shared by the Diligencias table, the detail page, the
// Causas surfaces and the dashboard, so every surface tells the same story.
//
// The phase comes named from the server: `tasks.stage` (the state machine's
// eight values) replaces the old habit of crossing the legacy `status` with
// quote tallies and the payin status. What stays derived HERE is only what the
// stored row cannot say at read time — the two date overlays:
//   · Expirada  — the deadline passed while the diligencia never reached
//     "en curso" (any pre-IN_PROGRESS stage). The hourly sweep eventually
//     persists the terminal; this reading covers the gap.
//   · Atrasada / cierre natural — an IN_PROGRESS whose committed date or legal
//     deadline passed (rules below).

import {
  MS_PER_DAY,
  deadlineCalendarDays,
  formatDeadlineDate,
  isDeadlineExpired,
} from './dateUtils';

// Stages before the money committed: a deadline passing here kills the
// diligencia ("quedó a medias") instead of concluding it. IN_PROGRESS is
// deliberately absent — past its deadline it closed naturally (Finalizada).
const PRE_COMMITMENT_STAGES = new Set([
  'DRAFT',
  'QUOTING',
  'PAYMENT_DUE',
  'PAYMENT_PROCESSING',
  'PAYMENT_REJECTED',
]);

function isDiligenciaExpired(stage, deadlineIso, now = Date.now(), expiresAt) {
  if (!PRE_COMMITMENT_STAGES.has((stage || '').toUpperCase())) return false;
  return (
    isDeadlineExpired(deadlineIso, expiresAt, now) ?? isPast(deadlineIso, now)
  );
}

// Instantes reales (dateOffered) y respaldo exacto de staging para el plazo.
function isPast(iso, now = Date.now()) {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return !Number.isNaN(t) && t < now;
}

// The three visual states of an IN_PROGRESS diligencia, derived ONCE so the
// stagepill tone (diligenciaStage), the estado reading (this module) and the
// table's DeadlineCell tell the same story:
//   'deadline-late'   — plazo legal vencido → the row reads Finalizada (cierre
//                       natural) and the date cell keeps the red reading;
//   'commitment-late' — dateOffered passed, plazo still valid → warn tone;
//   'ontrack'         — neither date passed.
// Returns null for every other stage: pre-commitment lateness is Expirada
// territory and terminal rows have no lateness to tell.
export function inProgressLateness(
  stage,
  deadlineIso,
  dateOffered,
  now = Date.now(),
  expiresAt,
) {
  if ((stage || '').toUpperCase() !== 'IN_PROGRESS') return null;
  if (
    isDeadlineExpired(deadlineIso, expiresAt, now) ??
    isPast(deadlineIso, now)
  )
    return 'deadline-late';
  if (isPast(dateOffered, now)) return 'commitment-late';
  return 'ontrack';
}

// Deadline formatting for the table's "Diligencia" column headline and the
// drawer's "Plazo" field: a short date, a full date, a compact relative delta
// ("en 7 d" / "hace 4 d" / "hoy"), and the urgency class for the .plazo color
// (tight=red ≤2d/past, medium=amber ≤7d, loose=neutral).
export function formatDeadline(iso, now = Date.now(), expiresAt) {
  return formatDateSummary(iso, now, true, expiresAt);
}

// Los cierres y actualizaciones son instantes reales y conservan hora local.
export function formatHistoryDate(iso, now = Date.now()) {
  return formatDateSummary(iso, now, false);
}

function formatDateSummary(iso, now, calendar, expiresAt) {
  if (!iso)
    return { short: '—', full: '—', numeric: '—', delta: '', urgency: 'loose' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return { short: '—', full: '—', numeric: '—', delta: '', urgency: 'loose' };
  }
  const short = calendar
    ? formatDeadlineDate(iso, 'short')
    : d.toLocaleDateString('es-CL', {
        day: '2-digit',
        month: 'short',
      });
  const full = calendar
    ? formatDeadlineDate(iso)
    : d.toLocaleDateString('es-CL', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
  // Fecha numérica DD/MM/YYYY (con diagonal) — titular del plazo en la tabla de
  // /diligencias.
  const p = (n) => String(n).padStart(2, '0');
  const numeric = calendar
    ? formatDeadlineDate(iso, 'numeric')
    : `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
  const days =
    (calendar ? deadlineCalendarDays(iso, now, expiresAt) : null) ??
    Math.round((d.getTime() - now) / MS_PER_DAY);
  let delta;
  if (days === 0) delta = 'hoy';
  else if (days < 0) delta = `hace ${Math.abs(days)} d`;
  else delta = `en ${days} d`;
  const urgency = days <= 2 ? 'tight' : days <= 7 ? 'medium' : 'loose';
  return { short, full, numeric, delta, urgency };
}

// Per-stage display model — ONE entry per `tasks.stage` value, the single home
// of the Spanish labels.
//   dot   → StatusDotPill `status` (s-draft|s-info|s-pay|s-live|s-ok|s-err)
//   badge → RightSidebar Badge `kind` (muted|info|pay|live|ok|err)
//   sub   → concise one-liner for the table's estado subtext
//   alert → fuller prose for the sidebar's always-on state alert box (explains
//           what is happening, in plain language)
//   tone  → est-sub modifier in the table ('warn' | 'err' | undefined)
//   row   → Ledger <tr> tint via row.status ('err' | undefined)
const BASE = {
  DRAFT: {
    dot: 'draft',
    badge: 'muted',
    label: 'Borrador',
    sub: 'Aún sin enviar a cotizar.',
    alert:
      'Esta diligencia es un borrador: aún no se ha enviado a cotizar. Envíala para que los receptores de la corte puedan ofertar.',
  },
  QUOTING: {
    dot: 'info',
    badge: 'info',
    label: 'Cotizando',
    sub: 'Esperando ofertas de receptores.',
    alert:
      'Enviada a cotizar. Esperando que los receptores de la corte respondan con sus ofertas (honorarios y fecha).',
  },
  // Oferta elegida esperando el pago del estudio. Usa s-info (azul = "necesita
  // acción", el acento interactivo canónico).
  PAYMENT_DUE: {
    dot: 'info',
    badge: 'info',
    label: 'Listo para pagar',
    sub: 'Oferta aceptada · pendiente de pago.',
    alert:
      'Hay una oferta aceptada esperando pago. Paga para que el receptor pueda realizar la actuación.',
  },
  // Cobro iniciado y sin confirmación del proveedor. La copy NO afirma "pago
  // recibido": sería falsa mientras el payin no está confirmado (p. ej. si el
  // estudio aún no completa el checkout).
  PAYMENT_PROCESSING: {
    dot: 'pay',
    badge: 'pay',
    label: 'Procesando pago',
    sub: 'Pago en proceso.',
    alert:
      'El pago está en proceso. Cuando se confirme, el receptor realiza la actuación.',
    tone: 'warn',
  },
  // Cobro rechazado o abandonado. No reabre la elección: la diligencia sigue
  // comprometida con la oferta elegida; reintentar el MISMO pago vive en el
  // detalle (CheckoutModal), reutilizando el link. Usa s-err (acción
  // correctiva).
  PAYMENT_REJECTED: {
    dot: 'err',
    badge: 'err',
    label: 'Reintentar pago',
    sub: 'Pago no completado · reintenta en el detalle.',
    alert:
      'El pago no se completó. Reintenta el mismo pago desde el detalle; las otras ofertas quedan bloqueadas mientras tanto.',
    tone: 'err',
  },
  IN_PROGRESS: {
    dot: 'live',
    badge: 'live',
    label: 'En curso',
    sub: 'Pagada · esperando la actuación del receptor.',
    // Doesn't claim the receptor was already paid: the stage moves to
    // IN_PROGRESS when the payin confirms, and the payout to the receptor may
    // still be pending.
    alert:
      'Pagada. La diligencia está en curso: el receptor realizará la actuación dentro del plazo.',
  },
  CLOSED: {
    dot: 'ok',
    badge: 'ok',
    label: 'Finalizada',
    sub: 'Plazo cumplido · diligencia concluida.',
    alert:
      'Diligencia concluida: el plazo se cumplió y la actuación quedó cerrada.',
  },
  // Cancelada: la abandonó el estudio antes del pago (cancelación). Terminal y
  // neutra — no es un éxito (Finalizada) ni un vencimiento (Expirada); libera
  // la causa. Dot/badge muted (gris), sin tono de alerta.
  CANCELLED: {
    dot: 'draft',
    badge: 'muted',
    label: 'Cancelada',
    sub: 'Cancelada por el estudio · previa al pago.',
    alert:
      'La diligencia fue cancelada por el estudio antes del pago. La causa quedó libre para volver a cotizar.',
  },
};

// The one label map per stage — the Causas filter builds its options from
// this, so the filter and every pill say the same word.
export const STAGE_LABELS = Object.fromEntries(
  Object.entries(BASE).map(([stage, { label }]) => [stage, label]),
);

const EXPIRED = {
  dot: 'err',
  badge: 'err',
  label: 'Expirada',
  sub: 'Plazo vencido · no admite ofertas ni pagos.',
  // No promete un reenvío: una vez que salió a cotizar, sus datos (incluida
  // la fecha límite) quedan fijos — el backend no admite editarla. El único
  // camino es cancelarla (libera la causa) y crear una diligencia nueva.
  alert:
    'Diligencia vencida. No admite nuevas ofertas y no se puede pagar ninguna cotización. Cancélala para liberar la causa y solicita una nueva diligencia.',
  tone: 'err',
  row: 'err',
};

// "Atrasada" — sub-estado DERIVADO de una diligencia EN CURSO cuya fecha
// comprometida por el receptor (dateOffered) ya pasó, PERO el plazo legal
// (deadline) sigue vigente. Sigue viva y en camino, solo va con retraso →
// aviso suave (ámbar). NO es expirada: solo el deadline expira algo.
const ATRASADA = {
  dot: 'warn',
  badge: 'warn',
  label: 'Atrasada',
  sub: 'En curso · fecha comprometida vencida.',
  alert:
    'La diligencia está en curso, pero la fecha comprometida por el receptor ya pasó. El plazo legal sigue vigente: el receptor debe completar la actuación cuanto antes.',
  tone: 'warn',
  row: 'warn',
};

const FALLBACK = {
  dot: 'info',
  badge: 'muted',
  label: '—',
  sub: '',
  alert: '',
};

// Returns { dot, badge, label, sub, alert, tone?, row?, expired }.
//
// ───────────────────────────────────────────────────────────────────────────
// CORE BUSINESS RULES — the single source of truth for how a diligencia stage
// reads. The stage names the phase; only the date overlays are derived here:
//
//   1. On payin confirmation the diligencia is "En curso" for the solicitante,
//      ALWAYS — regardless of the payout. The payout (Recepthor→receptor,
//      manual via backoffice) and any payout failure are backoffice's concern,
//      NEVER surfaced to the solicitante here.
//   2. ONLY the `deadline` (plazo legal) triggers Expirada / cierre natural.
//      The `dateOffered` (the receptor's promised date) ONLY triggers
//      "Atrasada".
//   3. Expirada = the deadline passed AND the diligencia never reached
//      "en curso" (any pre-IN_PROGRESS stage: "quedó a medias"). It died
//      before committing.
//   4. IN_PROGRESS + deadline passed = CIERRE NATURAL → reads Finalizada (the
//      plazo fulfilled concludes the process; product rule). Atrasada = the
//      diligencia IS en curso, dateOffered passed but deadline still valid.
//      → An IN_PROGRESS diligencia is NEVER "Expirada".
//
// Precedence: pre-commitment stages check the deadline FIRST → Expirada
// overrides their base reading. IN_PROGRESS: deadline passed → Finalizada
// (cierre); elif dateOffered passed → Atrasada; else En curso. CLOSED /
// CANCELLED → terminal readings.
// ───────────────────────────────────────────────────────────────────────────
//
// `opts.dateOffered` (ISO, the paid quote's committed date) drives the
// IN_PROGRESS "Atrasada" distinction; null/absent → no Atrasada (only
// Finalizada / En curso).
export function diligenciaDisplay(
  stage,
  deadlineIso,
  now = Date.now(),
  { dateOffered = null, deadlineExpiresAt } = {},
) {
  // Rule 3 — Expirada (pre-commitment + deadline passed) overrides the base
  // reading, a rejected payment included.
  if (isDiligenciaExpired(stage, deadlineIso, now, deadlineExpiresAt)) {
    return { ...EXPIRED, expired: true };
  }
  const s = (stage || '').toUpperCase();
  // Rule 4 — deadline passed on an IN_PROGRESS = cierre natural → Finalizada.
  // Only dateOffered (receptor's promise) produces the derived "Atrasada".
  // The split lives in inProgressLateness so pill, subtext and DeadlineCell
  // read the same derivation.
  if (s === 'IN_PROGRESS') {
    const late = inProgressLateness(
      s,
      deadlineIso,
      dateOffered,
      now,
      deadlineExpiresAt,
    );
    if (late === 'deadline-late') return { ...BASE.CLOSED, expired: false };
    if (late === 'commitment-late') return { ...ATRASADA, expired: false };
  }
  const base = BASE[s] ?? { ...FALLBACK, label: stage || '—' };
  return { ...base, expired: false };
}
